import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Flex, Image, Menu } from "@mantine/core";
import { IconCopy, IconPlus } from "@tabler/icons-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import flags from "assets/flags";
import { Button, EmptyMessage, Loader, Text } from "components";
import { IRootState } from "config/redux";
import { AppConstants, ServiceConstants } from "constant";
import { CreditRequestAttachmentTypeResponse, CreditResponse } from "types";
import { AppUtils, StorageUtils } from "utils";

import CreditRequiredDocumentationItem from "./item";
import AttachmentTypeCreateModal from "./new";

const { STORAGE_USER, STORAGE_USER_DOCUMENTATION_LANGUAGE } = AppConstants;
const { REQUEST_CREDIT_ATTACHMENT_TYPE_NEW_ID } = ServiceConstants;

const languages = [
  { key: "DE", flag: flags.de },
  { key: "EN", flag: flags.en },
  { key: "SR", flag: flags.sr },
];

interface CreditRequiredDocumentationProps {
  credit: CreditResponse;
  types?: CreditRequestAttachmentTypeResponse[];
  loading: boolean;
  isNewAllowed: boolean;
}

const CreditRequiredDocumentation = ({
  credit,
  loading,
  types,
  isNewAllowed,
}: CreditRequiredDocumentationProps) => {
  const { t } = useTranslation();

  const user = StorageUtils.getObject(STORAGE_USER);
  const createServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENT_TYPE_NEW_ID],
  );

  const [tempTypes, setTempTypes] = useState<CreditRequestAttachmentTypeResponse[] | undefined>(
    undefined,
  );
  const [newOpened, setNewOpened] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>(user.language);

  const getName = useCallback(
    (item: CreditRequestAttachmentTypeResponse) => {
      let localizedMessage = item.name;
      if (item.localizationKey) {
        localizedMessage = t(`credit.documentation.type.${item.localizationKey}`, {
          lng: language.toLowerCase(),
        });
        if (item.localizationParams) {
          const localizationParams = item.localizationParams.split(",");
          localizedMessage = localizedMessage
            .replace(/{{date,\s*(.*?),\s*'(.*?)'}}/g, (_, format, separator) => {
              return localizationParams
                .map((date) => moment(date).locale(language).format(format))
                .join(separator);
            })
            // Handle single dates by index, e.g. {{date0, yyyy}}, {{date1, MMMM yyyy}}
            .replace(/{{date(\d+),\s*(.*?)}}/g, (_, index, format) => {
              const date = localizationParams[parseInt(index, 10)];
              return date ? moment(date).locale(language).format(format) : "";
            });
        }

        if (item.additionalLocalizationKey) {
          let additional = "";
          item.additionalLocalizationKey.split(",").forEach((el) => {
            if (additional.length > 0) {
              additional += ", ";
            }
            additional += t(
              `credit.documentation.type.additional.${item.type.toLowerCase()}.${el}`,
              { lng: language.toLowerCase() },
            );
          });
          localizedMessage += " (" + additional + ")";
        }
        if (item.partner) {
          localizedMessage +=
            " - " +
            t(`credit.documentation.type.additional.partner`, {
              lng: language.toLowerCase(),
            });
        }
        return localizedMessage;
      }
      return item.name;
    },
    [language, t],
  );

  useEffect(() => {
    const storageLanguage = StorageUtils.getString(STORAGE_USER_DOCUMENTATION_LANGUAGE);
    if (storageLanguage) {
      setLanguage(storageLanguage);
    }
  }, []);

  useEffect(() => {
    setTempTypes(types);
  }, [types]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      createServiceObject,
      REQUEST_CREDIT_ATTACHMENT_TYPE_NEW_ID,
      (val) => {
        setTempTypes((old) => {
          const oldTypes = old ? [...old] : [];
          oldTypes.push(val);
          return oldTypes;
        });
        setNewOpened(false);
      },
    );
  }, [createServiceObject]);

  const rows = useMemo(() => {
    if (!tempTypes) {
      return <></>;
    }
    return tempTypes.map((el) => (
      <CreditRequiredDocumentationItem
        key={el.id}
        creditId={credit.id}
        item={el}
        language={language}
        getName={getName}
        isEditAllowed={isNewAllowed}
      />
    ));
  }, [tempTypes, language, isNewAllowed, credit.id, getName]);

  const changeLanguage = useCallback((lang: string) => {
    StorageUtils.setString(STORAGE_USER_DOCUMENTATION_LANGUAGE, lang);
    setLanguage(lang);
  }, []);

  const copyToClipboard = useCallback(() => {
    if (tempTypes) {
      navigator.clipboard.writeText(
        tempTypes
          .filter((el) => {
            const item = document.getElementById(`credit-attachment-type-${el.id}`);
            return !item?.children[0].getAttribute("data-checked");
          })
          .map((el) => getName(el))
          .join("\n"),
      );
    }
  }, [tempTypes, getName]);

  return (
    <>
      <Flex align={"center"} justify={"space-between"} mb={"sm"}>
        <Box>
          <Flex align={"center"} mr={"md"}>
            <Text ml={"md"}>{t("credit.documentation.required.title")}</Text>
            <Button onClick={copyToClipboard} variant={"transparent"} size={"compact-xs"}>
              <IconCopy size={18} color={"var(--mantine-color-black)"} />
            </Button>
            <Menu>
              <Menu.Target>
                <Button variant={"transparent"} size={"compact-xs"}>
                  <Image
                    src={languages.find((el) => el.key === language)?.flag}
                    height={20}
                    width={20}
                    bd={"0.5px solid lightgray"}
                    radius={20}
                    style={{ width: 20, height: 20 }}
                  />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {languages.map((el, index) => (
                  <Menu.Item
                    leftSection={
                      <Image
                        src={el.flag}
                        height={20}
                        width={20}
                        bd={"0.5px solid lightgray"}
                        radius={20}
                        style={{ width: 20, height: 20 }}
                      />
                    }
                    key={`language-${index}`}
                    onClick={() => changeLanguage(el.key)}
                  >
                    {t(`user.language.options.${el.key}`)}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Box>
        {isNewAllowed && (
          <>
            <Button onClick={() => setNewOpened(true)} size="compact-xs">
              <IconPlus />
            </Button>
            <AttachmentTypeCreateModal
              opened={newOpened}
              onClose={() => setNewOpened(false)}
              creditId={credit.id}
              t={t}
            />
          </>
        )}
      </Flex>
      <Box bg={"var(--mantine-color-gray-1)"} style={{ borderRadius: 8 }} p={"md"}>
        <Box pos={"relative"}>
          <Loader overlay={true} visible={!tempTypes || loading} />
          {tempTypes && tempTypes.length === 0 ? (
            <EmptyMessage message={t("credit.documentation.required.list.empty")} />
          ) : (
            rows
          )}
        </Box>
      </Box>
    </>
  );
};

export default CreditRequiredDocumentation;
